// InventoryDashboard.jsx
// Main inventory dashboard with product list and expandable rows for items

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import "react-bootstrap-table/css/react-bootstrap-table.css";
import InventoryAPI from "../../../services/inventoryService";

const DESKTOP_BREAKPOINT = 1024;

class InventoryDashboard extends Component {
    state = {
        typeId: this.props.typeId,
        products: [],
        categories: [],
        units: [],

        // Search/Filter
        searchTerm: "",
        selectedCategoryFilter: "All Categories",

        // Expanded rows for showing items
        expandedRows: [],
        productItems: {},

        // Add/Edit product modal
        showProductModal: false,
        editingProduct: null,
        productForm: {
            name: '',
            brand: '',
            category_id: '',
            reorder_point: 0
        },
        showAddBrand: false,
        newBrand: '',
        showAddProduct: false,
        newProduct: '',

        // Adjust quantity modal
        showAdjustModal: false,
        adjustingItem: null,
        adjustQuantity: '',

        // Usage history modal
        showUsageModal: false,
        usageProduct: null,
        usageData: [],
        loadingUsage: false,

        // Responsive state
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,
        loading: true,
        saving: false,
        error: null
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadData = async () => {
        try {
            // Use installer ID for inventory (not location-based yet)
            const [stockLevels, categories, units] = await Promise.all([
                InventoryAPI.getStockLevels(this.props.typeId),
                InventoryAPI.getServiceCategories(),
                InventoryAPI.getUnitsOfMeasure()
            ]);

            this.setState({
                products: stockLevels || [],
                categories: categories || [],
                units: units || [],
                loading: false
            });
        } catch (error) {
            console.error('Error loading inventory data:', error);
            this.setState({
                error: 'Failed to load inventory data. Please refresh the page.',
                loading: false
            });
        }
    };

    // =========================================================================
    // Metrics
    // =========================================================================

    // Helper to get stock quantity from product (handles different API field names)
    getProductQuantity = (p) => {
        return parseFloat(p.quantity_on_hand || p.total_quantity || p.quantity_in_stock || p.stock_quantity || p.quantity || 0);
    };

    getMetrics = () => {
        const { products } = this.state;
        const totalProducts = products.length;
        const lowStockCount = products.filter(p => {
            const qty = this.getProductQuantity(p);
            const reorder = parseFloat(p.reorder_point) || 0;
            return reorder > 0 && qty <= reorder;
        }).length;
        const totalItems = products.reduce((sum, p) => sum + (parseInt(p.item_count) || 0), 0);

        return { totalProducts, lowStockCount, totalItems };
    };

    // =========================================================================
    // Search/Filter
    // =========================================================================

    handleSearchChange = (e) => {
        this.setState({ searchTerm: e.target.value });
    };

    handleCategoryFilterChange = (e) => {
        this.setState({ selectedCategoryFilter: e.target.value });
    };

    clearSearch = () => {
        this.setState({ searchTerm: "" });
    };

    getFilteredProducts = () => {
        const { products, searchTerm, selectedCategoryFilter } = this.state;
        let filtered = [...products];

        // Filter by search term (handle both field naming conventions)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(p => {
                const name = (p.product_name || p.name || '').toLowerCase();
                const brand = (p.brand || '').toLowerCase();
                const sku = (p.sku || '').toLowerCase();
                return name.includes(term) || brand.includes(term) || sku.includes(term);
            });
        }

        // Filter by category
        if (selectedCategoryFilter !== "All Categories") {
            filtered = filtered.filter(p =>
                p.category_name === selectedCategoryFilter ||
                String(p.category_id) === selectedCategoryFilter
            );
        }

        // Sort by category, then by brand, then by product name
        filtered.sort((a, b) => {
            const categoryA = (a.category_name || '').toLowerCase();
            const categoryB = (b.category_name || '').toLowerCase();
            const categoryCompare = categoryA.localeCompare(categoryB);
            if (categoryCompare !== 0) return categoryCompare;

            const brandA = (a.brand || '').toLowerCase();
            const brandB = (b.brand || '').toLowerCase();
            const brandCompare = brandA.localeCompare(brandB);
            if (brandCompare !== 0) return brandCompare;

            const nameA = (a.product_name || a.name || '').toLowerCase();
            const nameB = (b.product_name || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        return filtered;
    };

    // =========================================================================
    // Expandable Rows
    // =========================================================================

    handleToggleExpand = async (productId) => {
        const { expandedRows, productItems } = this.state;
        const isExpanded = expandedRows.includes(productId);

        if (isExpanded) {
            this.setState({
                expandedRows: expandedRows.filter(id => id !== productId)
            });
        } else {
            // Load items if not already loaded
            if (!productItems[productId]) {
                try {
                    const items = await InventoryAPI.getInventoryItems(productId, this.props.typeId);
                    this.setState(prev => ({
                        productItems: {
                            ...prev.productItems,
                            [productId]: items || []
                        },
                        expandedRows: [...prev.expandedRows, productId]
                    }));
                } catch (error) {
                    console.error('Error loading items:', error);
                    this.setState({
                        expandedRows: [...expandedRows, productId]
                    });
                }
            } else {
                this.setState({
                    expandedRows: [...expandedRows, productId]
                });
            }
        }
    };

    // =========================================================================
    // Product Modal
    // =========================================================================

    handleOpenAddProduct = () => {
        this.setState({
            showProductModal: true,
            editingProduct: null,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: '',
            productForm: {
                name: '',
                brand: '',
                category_id: '',
                reorder_point: 0
            }
        });
    };

    handleOpenEditProduct = (product) => {
        this.setState({
            showProductModal: true,
            editingProduct: product,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: '',
            productForm: {
                name: product.product_name || product.name || '',
                brand: product.brand || '',
                category_id: product.category_id || '',
                reorder_point: product.reorder_point || 0
            }
        });
    };

    handleCloseProductModal = () => {
        this.setState({
            showProductModal: false,
            editingProduct: null,
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProduct: ''
        });
    };

    // Get unique brands from existing products, filtered by category if provided
    getUniqueBrands = (categoryId = null) => {
        const { products } = this.state;
        let filteredProducts = products;

        // Filter by category if a category is selected
        if (categoryId) {
            filteredProducts = products.filter(p =>
                String(p.category_id) === String(categoryId)
            );
        }

        const brands = filteredProducts
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, index, self) => self.indexOf(brand) === index)
            .sort();
        return brands;
    };

    // Get unique product names filtered by category and brand
    getUniqueProductNames = (categoryId = null, brand = null) => {
        const { products } = this.state;
        let filteredProducts = products;

        // Filter by category
        if (categoryId) {
            filteredProducts = filteredProducts.filter(p =>
                String(p.category_id) === String(categoryId)
            );
        }

        // Filter by brand
        if (brand) {
            filteredProducts = filteredProducts.filter(p =>
                p.brand === brand
            );
        }

        const names = filteredProducts
            .map(p => p.product_name || p.name)
            .filter(name => name && name.trim())
            .filter((name, index, self) => self.indexOf(name) === index)
            .sort();
        return names;
    };

    handleBrandChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddBrand: true,
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, brand: '', name: '' }
            });
        } else {
            this.setState({
                showAddBrand: false,
                newBrand: '',
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, brand: value, name: '' }
            });
        }
    };

    handleNewBrandChange = (e) => {
        const value = e.target.value;
        this.setState({
            newBrand: value,
            productForm: { ...this.state.productForm, brand: value }
        });
    };

    handleCancelNewBrand = () => {
        this.setState({
            showAddBrand: false,
            newBrand: '',
            productForm: { ...this.state.productForm, brand: '' }
        });
    };

    handleProductNameChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddProduct: true,
                productForm: { ...this.state.productForm, name: '' }
            });
        } else {
            this.setState({
                showAddProduct: false,
                newProduct: '',
                productForm: { ...this.state.productForm, name: value }
            });
        }
    };

    handleNewProductChange = (e) => {
        const value = e.target.value;
        this.setState({
            newProduct: value,
            productForm: { ...this.state.productForm, name: value }
        });
    };

    handleCancelNewProduct = () => {
        this.setState({
            showAddProduct: false,
            newProduct: '',
            productForm: { ...this.state.productForm, name: '' }
        });
    };

    handleProductFormChange = (field, value) => {
        this.setState(prev => {
            const newForm = {
                ...prev.productForm,
                [field]: value
            };

            // Reset brand and product name when category changes (cascading selection)
            if (field === 'category_id') {
                newForm.brand = '';
                newForm.name = '';
            }

            return {
                productForm: newForm,
                // Also reset add brand/product mode when category changes
                showAddBrand: field === 'category_id' ? false : prev.showAddBrand,
                newBrand: field === 'category_id' ? '' : prev.newBrand,
                showAddProduct: field === 'category_id' ? false : prev.showAddProduct,
                newProduct: field === 'category_id' ? '' : prev.newProduct
            };
        });
    };

    handleSaveProduct = async () => {
        const { editingProduct, productForm, categories, products } = this.state;

        if (!productForm.name.trim()) {
            alert('Product name is required');
            return;
        }

        // Check for duplicate product name + brand combination (case-insensitive) when creating new product
        if (!editingProduct) {
            const newName = productForm.name.trim().toLowerCase();
            const newBrand = (productForm.brand || '').trim().toLowerCase();
            const duplicate = products.find(p => {
                const existingName = (p.product_name || p.name || '').toLowerCase();
                const existingBrand = (p.brand || '').toLowerCase();
                return existingName === newName && existingBrand === newBrand;
            });
            if (duplicate) {
                const brandText = productForm.brand ? ` with brand "${productForm.brand.trim()}"` : '';
                alert(`A product named "${productForm.name.trim()}"${brandText} already exists.`);
                return;
            }
        }

        this.setState({ saving: true });

        try {
            // Get unitId from the selected category's default unit
            let unitId = null;
            if (productForm.category_id) {
                const selectedCategory = categories.find(c =>
                    String(c.category_id) === String(productForm.category_id)
                );
                if (selectedCategory) {
                    // Try both snake_case and camelCase field names
                    unitId = selectedCategory.default_unit_id || selectedCategory.defaultUnitId;
                }
            }

            // Build product data with proper types (using camelCase for API)
            const productData = {
                name: productForm.name.trim(),
                brand: productForm.brand || '',
                categoryId: parseInt(productForm.category_id, 10) || null,
                unitId: unitId ? parseInt(unitId, 10) : null,
                reorderPoint: parseInt(productForm.reorder_point, 10) || 0,
                installerId: this.props.typeId
            };

            if (editingProduct) {
                const productId = editingProduct.product_id || editingProduct.id;
                await InventoryAPI.updateProduct(productId, productData);
            } else {
                await InventoryAPI.createProduct(productData);
            }

            await this.loadData();
            this.handleCloseProductModal();
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error saving product. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    handleDeleteProduct = async () => {
        const { editingProduct } = this.state;

        if (!editingProduct) return;

        const productId = editingProduct.product_id || editingProduct.id;
        const productName = editingProduct.product_name || editingProduct.name || '';

        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
            return;
        }

        this.setState({ saving: true });

        try {
            await InventoryAPI.deleteProduct(productId, this.props.typeId);
            await this.loadData();
            this.handleCloseProductModal();
        } catch (error) {
            console.error('Error deleting product:', error);
            const message = error.response?.data?.message || 'Error deleting product. Please try again.';
            alert(message);
        } finally {
            this.setState({ saving: false });
        }
    };

    // Check if a product can be deleted (no stock, no usage)
    canDeleteProduct = () => {
        const { editingProduct } = this.state;
        if (!editingProduct) return false;

        const quantity = parseFloat(editingProduct.quantity_on_hand || editingProduct.total_quantity || 0);
        const itemCount = parseInt(editingProduct.item_count) || 0;
        const usageCount = parseInt(editingProduct.usage_count) || 0;

        return quantity === 0 && itemCount === 0 && usageCount === 0;
    };

    // =========================================================================
    // Adjust Quantity Modal
    // =========================================================================

    handleOpenAdjustModal = (item, productId) => {
        this.setState({
            showAdjustModal: true,
            adjustingItem: { ...item, productId },
            adjustQuantity: item.quantity_remaining || ''
        });
    };

    handleCloseAdjustModal = () => {
        this.setState({
            showAdjustModal: false,
            adjustingItem: null,
            adjustQuantity: ''
        });
    };

    handleAdjustQuantityChange = (e) => {
        this.setState({ adjustQuantity: e.target.value });
    };

    handleSaveAdjustment = async () => {
        const { adjustingItem, adjustQuantity } = this.state;

        if (!adjustQuantity || parseFloat(adjustQuantity) < 0) {
            alert('Please enter a valid quantity');
            return;
        }

        this.setState({ saving: true });

        try {
            await InventoryAPI.adjustInventory({
                itemId: adjustingItem.item_id || adjustingItem.id,
                newQuantity: parseFloat(adjustQuantity)
            });

            // Reload items for this product
            const items = await InventoryAPI.getInventoryItems(adjustingItem.productId, this.props.typeId);

            this.setState(prev => ({
                productItems: {
                    ...prev.productItems,
                    [adjustingItem.productId]: items || []
                }
            }));

            // Reload main data to update totals
            await this.loadData();
            this.handleCloseAdjustModal();
        } catch (error) {
            console.error('Error adjusting inventory:', error);
            alert('Error adjusting inventory. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    // =========================================================================
    // Usage History Modal
    // =========================================================================

    handleOpenUsageModal = async (product) => {
        const productId = product.product_id || product.id;

        this.setState({
            showUsageModal: true,
            usageProduct: product,
            usageData: [],
            loadingUsage: true
        });

        try {
            const usageData = await InventoryAPI.getProductUsage(productId, this.props.typeId);
            this.setState({
                usageData: usageData || [],
                loadingUsage: false
            });
        } catch (error) {
            console.error('Error loading usage data:', error);
            this.setState({
                usageData: [],
                loadingUsage: false
            });
        }
    };

    handleCloseUsageModal = () => {
        this.setState({
            showUsageModal: false,
            usageProduct: null,
            usageData: [],
            loadingUsage: false
        });
    };

    handleOpenItemUsageModal = async (item, product) => {
        const itemId = item.item_id || item.id;

        this.setState({
            showUsageModal: true,
            usageProduct: {
                ...product,
                // Override with item-specific info for display
                isItemLevel: true,
                barcode: item.barcode,
                item_id: itemId
            },
            usageData: [],
            loadingUsage: true
        });

        try {
            const usageData = await InventoryAPI.getItemUsage(itemId, this.props.typeId);
            this.setState({
                usageData: usageData || [],
                loadingUsage: false
            });
        } catch (error) {
            console.error('Error loading item usage data:', error);
            this.setState({
                usageData: [],
                loadingUsage: false
            });
        }
    };

    // =========================================================================
    // CSV Export
    // =========================================================================

    handleExportCSV = async () => {
        const { products } = this.state;

        if (products.length === 0) {
            alert('No products to export');
            return;
        }

        this.setState({ saving: true });

        try {
            // Fetch all items for all products
            const allData = [];

            for (const product of products) {
                const productId = product.product_id || product.id;
                const productName = product.product_name || product.name || '';
                const brand = product.brand || '';
                const category = product.category_name || '';
                const quantityOnHand = parseFloat(product.quantity_on_hand || product.total_quantity || 0).toFixed(2);
                const reorderPoint = product.reorder_point || '';
                const usageCount = product.usage_count || 0;

                // Fetch items for this product
                let items = [];
                try {
                    items = await InventoryAPI.getInventoryItems(productId, this.props.typeId);
                } catch (err) {
                    console.error(`Error fetching items for product ${productId}:`, err);
                }

                if (items && items.length > 0) {
                    // Add a row for each item
                    for (const item of items) {
                        allData.push({
                            category,
                            brand,
                            productName,
                            quantityOnHand,
                            reorderPoint,
                            usageCount,
                            barcode: item.barcode || '',
                            amountRemaining: parseFloat(item.quantity_remaining || 0).toFixed(2),
                            dateReceived: item.created_at ? new Date(item.created_at).toLocaleDateString() : ''
                        });
                    }
                } else {
                    // Add a row for the product even if no items
                    allData.push({
                        category,
                        brand,
                        productName,
                        quantityOnHand,
                        reorderPoint,
                        usageCount,
                        barcode: '',
                        amountRemaining: '',
                        dateReceived: ''
                    });
                }
            }

            // Generate CSV content
            const headers = [
                'Category',
                'Brand',
                'Product Name',
                'Total In Stock',
                'Reorder Point',
                'Jobs Used',
                'Item Barcode',
                'Amount Remaining',
                'Date Received'
            ];

            const csvRows = [headers.join(',')];

            for (const row of allData) {
                const values = [
                    `"${row.category}"`,
                    `"${row.brand}"`,
                    `"${row.productName}"`,
                    row.quantityOnHand,
                    row.reorderPoint,
                    row.usageCount,
                    `"${row.barcode}"`,
                    row.amountRemaining,
                    `"${row.dateReceived}"`
                ];
                csvRows.push(values.join(','));
            }

            const csvContent = csvRows.join('\n');

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Error exporting inventory. Please try again.');
        } finally {
            this.setState({ saving: false });
        }
    };

    // =========================================================================
    // Render Methods
    // =========================================================================

    renderOverviewCards() {
        const { totalProducts, lowStockCount, totalItems } = this.getMetrics();

        return (
            <div className="overview-cards">
                <div className={`overview-card ${lowStockCount > 0 ? 'warning' : ''}`}>
                    <div className="number">{lowStockCount}</div>
                    <div className="label">Low Stock</div>
                </div>
                <div className="overview-card">
                    <div className="number">{totalProducts}</div>
                    <div className="label">Products</div>
                </div>
                <div className="overview-card">
                    <div className="number">{totalItems}</div>
                    <div className="label">Total Items</div>
                </div>
            </div>
        );
    }

    renderFiltersSection() {
        const { searchTerm, selectedCategoryFilter, categories } = this.state;

        const categoryOptions = ["All Categories", ...new Set(
            categories.map(c => c.category_name || c.name).filter(Boolean)
        )].sort((a, b) => a === "All Categories" ? -1 : b === "All Categories" ? 1 : a.localeCompare(b));

        return (
            <div className="filters-section">
                <div className="search-controls">
                    <div className="search-group">
                        <label htmlFor="productSearch">Search:</label>
                        <div className="search-input-group">
                            <input
                                type="text"
                                className="search-input"
                                id="productSearch"
                                placeholder="Search by name, brand, or SKU..."
                                value={searchTerm}
                                onChange={this.handleSearchChange}
                            />
                            {searchTerm && (
                                <button
                                    className="clear-search-btn"
                                    onClick={this.clearSearch}
                                    type="button"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="filter-controls">
                    <div className="filter-group">
                        <label>Category:</label>
                        <select
                            className="form-control"
                            value={selectedCategoryFilter}
                            onChange={this.handleCategoryFilterChange}
                        >
                            {categoryOptions.map((opt, idx) => (
                                <option key={idx} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    renderActionButtons() {
        const { saving } = this.state;

        return (
            <div className="action-buttons">
                <div className="label">Quick Actions:</div>
                <Link to="/inventory/receive" className="action-btn primary-btn">
                    <i className="fa fa-plus mr-2"></i>
                    Receive Inventory
                </Link>
                <button className="action-btn" onClick={this.handleOpenAddProduct}>
                    <i className="fa fa-cube mr-2"></i>
                    Add Product
                </button>
                <button
                    className="action-btn"
                    onClick={this.handleExportCSV}
                    disabled={saving}
                >
                    <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`}></i>
                    {saving ? 'Exporting...' : 'Export CSV'}
                </button>
            </div>
        );
    }

    // Helper to get unit name from unit_id
    getUnitName = (unitId) => {
        const { units } = this.state;
        const unit = units.find(u => String(u.unit_id) === String(unitId));
        return unit ? unit.unit_name : '';
    };

    renderProductModal() {
        const { showProductModal, editingProduct, productForm, categories, saving, showAddBrand, newBrand, showAddProduct, newProduct } = this.state;
        // Filter brands by selected category (cascading selection)
        const existingBrands = this.getUniqueBrands(productForm.category_id);
        // Filter product names by selected category and brand
        const existingProductNames = this.getUniqueProductNames(productForm.category_id, productForm.brand);

        // Get unit name from the selected category's default unit
        let selectedUnitName = '';
        if (productForm.category_id) {
            const selectedCategory = categories.find(c => String(c.category_id) === String(productForm.category_id));
            if (selectedCategory) {
                // Try both snake_case and camelCase field names
                const defaultUnitId = selectedCategory.default_unit_id || selectedCategory.defaultUnitId;
                if (defaultUnitId) {
                    selectedUnitName = this.getUnitName(defaultUnitId);
                }
            }
        }

        // Determine if brand is selected (either from dropdown or new brand input)
        const hasBrandSelected = productForm.brand && productForm.brand.trim();

        return (
            <Modal show={showProductModal} onHide={this.handleCloseProductModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingProduct ? 'Edit Product' : 'Add Product'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {/* Category Selection - First */}
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Control
                                as="select"
                                value={productForm.category_id}
                                onChange={(e) => this.handleProductFormChange('category_id', e.target.value)}
                            >
                                <option value="">Select category...</option>
                                {/* Deduplicate by category_id and sort alphabetically */}
                                {[...new Map(categories.map(c => [c.category_id, c])).values()]
                                    .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''))
                                    .map(cat => (
                                        <option key={cat.category_id} value={cat.category_id}>
                                            {cat.category_name}
                                        </option>
                                    ))}
                            </Form.Control>
                        </Form.Group>

                        {/* Brand Selection - filtered by category */}
                        <Form.Group className="mb-3">
                            <Form.Label>Brand</Form.Label>
                            {!showAddBrand ? (
                                <Form.Control
                                    as="select"
                                    value={productForm.brand}
                                    onChange={this.handleBrandChange}
                                    disabled={!productForm.category_id}
                                >
                                    <option value="">
                                        {!productForm.category_id
                                            ? 'Select a category first...'
                                            : existingBrands.length === 0
                                                ? 'No brands for this category - add new'
                                                : 'Select a brand...'}
                                    </option>
                                    {existingBrands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                    {productForm.category_id && (
                                        <option value="add_new">+ Add New Brand</option>
                                    )}
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newBrand}
                                        onChange={this.handleNewBrandChange}
                                        placeholder="Enter new brand name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewBrand}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>

                        {/* Product Name - filtered by category and brand */}
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name *</Form.Label>
                            {!showAddProduct ? (
                                <Form.Control
                                    as="select"
                                    value={productForm.name}
                                    onChange={this.handleProductNameChange}
                                    disabled={!hasBrandSelected}
                                >
                                    <option value="">
                                        {!productForm.category_id
                                            ? 'Select a category first...'
                                            : !hasBrandSelected
                                                ? 'Select a brand first...'
                                                : existingProductNames.length === 0
                                                    ? 'No products for this brand - add new'
                                                    : 'Select a product...'}
                                    </option>
                                    {existingProductNames.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                    {hasBrandSelected && (
                                        <option value="add_new">+ Add New Product</option>
                                    )}
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newProduct}
                                        onChange={this.handleNewProductChange}
                                        placeholder="Enter new product name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewProduct}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Low Stock Alert
                                {selectedUnitName && <small className="text-muted ml-1">({selectedUnitName})</small>}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                value={productForm.reorder_point}
                                onChange={(e) => this.handleProductFormChange('reorder_point', e.target.value)}
                                placeholder="0"
                                style={{ maxWidth: '200px' }}
                            />
                            <Form.Text className="text-muted">
                                Alert when total stock falls below this amount
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div>
                        {editingProduct && this.canDeleteProduct() && (
                            <Button
                                variant="outline-danger"
                                onClick={this.handleDeleteProduct}
                                disabled={saving}
                            >
                                <i className="fa fa-trash mr-2"></i>
                                Delete Product
                            </Button>
                        )}
                    </div>
                    <div>
                        <Button variant="secondary" onClick={this.handleCloseProductModal} className="mr-2">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={this.handleSaveProduct}
                            disabled={saving}
                        >
                            {saving ? (
                                <><i className="fa fa-spinner fa-spin mr-2"></i>Saving...</>
                            ) : (
                                'Save Product'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }

    renderAdjustModal() {
        const { showAdjustModal, adjustingItem, adjustQuantity, saving } = this.state;

        if (!adjustingItem) return null;

        return (
            <Modal show={showAdjustModal} onHide={this.handleCloseAdjustModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Quantity</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <small className="text-muted">Barcode:</small>
                        <div><code>{adjustingItem.barcode}</code></div>
                    </div>
                    <Form.Group>
                        <Form.Label>Amount Remaining</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={adjustQuantity}
                            onChange={this.handleAdjustQuantityChange}
                            placeholder="Enter new quantity"
                            autoFocus
                        />
                        <Form.Text className="text-muted">
                            Current: {parseFloat(adjustingItem.quantity_remaining || 0).toFixed(2)}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseAdjustModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={this.handleSaveAdjustment}
                        disabled={saving}
                    >
                        {saving ? (
                            <><i className="fa fa-spinner fa-spin mr-2"></i>Saving...</>
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderUsageModal() {
        const { showUsageModal, usageProduct, usageData, loadingUsage } = this.state;

        if (!usageProduct) return null;

        const productName = usageProduct.product_name || usageProduct.name || '';
        const brand = usageProduct.brand || '';
        const displayName = brand ? `${brand} - ${productName}` : productName;
        const isItemLevel = usageProduct.isItemLevel;

        return (
            <Modal show={showUsageModal} onHide={this.handleCloseUsageModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Usage History {isItemLevel && '- Item'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="usage-product-info mb-3">
                        <strong>{displayName}</strong>
                        {isItemLevel && (
                            <div className="mt-1">
                                <code>{usageProduct.barcode}</code>
                            </div>
                        )}
                        <div className="text-muted small">
                            {usageProduct.category_name || 'Uncategorized'}
                        </div>
                    </div>

                    {loadingUsage ? (
                        <div className="text-center py-4">
                            <i className="fa fa-spinner fa-spin fa-2x"></i>
                            <p className="mt-2">Loading usage history...</p>
                        </div>
                    ) : usageData.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <i className="fa fa-inbox fa-2x mb-2"></i>
                            <p>No usage records found for this product.</p>
                        </div>
                    ) : (
                        <div className="usage-table-container">
                            <table className="usage-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Customer</th>
                                        <th>Vehicle</th>
                                        <th>Service</th>
                                        <th>Qty Used</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageData.map((record, index) => (
                                        <tr key={index}>
                                            <td>
                                                {record.usage_date
                                                    ? new Date(record.usage_date).toLocaleDateString()
                                                    : '-'}
                                            </td>
                                            <td>{record.client_name || '-'}</td>
                                            <td>{record.vehicle_info || '-'}</td>
                                            <td>{record.service_description || '-'}</td>
                                            <td>
                                                {parseFloat(record.quantity_used || 0).toFixed(2)}
                                                {record.unit_code && <small className="text-muted ml-1">{record.unit_code}</small>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseUsageModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { isDesktop, error, loading, expandedRows, productItems } = this.state;

        if (error) {
            return (
                <div className="inventory-container">
                    <div className="error-message">
                        <h3>Error Loading Inventory</h3>
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()}>Refresh Page</button>
                    </div>
                </div>
            );
        }

        if (loading) {
            return (
                <div className="inventory-container">
                    <div className="text-center py-5">
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-3">Loading inventory...</p>
                    </div>
                </div>
            );
        }

        // Normalize product data to handle different API field names
        const filteredProducts = this.getFilteredProducts().map(p => ({
            ...p,
            id: p.product_id || p.id,
            name: p.product_name || p.name,
            brand: p.brand || ''
        }));

        const columns = [
            {
                dataField: 'expand',
                text: '',
                formatter: (cell, row) => {
                    const rowId = row.product_id || row.id;
                    const isExpanded = expandedRows.includes(rowId);
                    const hasItems = parseInt(row.item_count) > 0;
                    return hasItems ? (
                        <span
                            className="expand-toggle"
                            onClick={(e) => { e.stopPropagation(); this.handleToggleExpand(rowId); }}
                        >
                            {isExpanded ? '▼' : '▶'}
                        </span>
                    ) : null;
                },
                headerStyle: { width: '40px' }
            },
            {
                dataField: 'category_name',
                text: 'Category'
            },
            {
                dataField: 'name',
                text: 'Product Name',
                formatter: (cell, row) => {
                    const productName = row.product_name || row.name || '';
                    const brand = row.brand || '';
                    return brand ? `${brand} - ${productName}` : productName;
                }
            },
            {
                dataField: 'quantity_on_hand',
                text: 'In Stock',
                formatter: (cell, row) => {
                    // Handle different possible field names from API
                    const qty = parseFloat(row.quantity_on_hand || row.total_quantity || row.quantity_in_stock || row.stock_quantity || row.quantity || cell) || 0;
                    const itemCount = parseInt(row.item_count) || 0;
                    const reorder = parseFloat(row.reorder_point) || 0;
                    const isLow = reorder > 0 && qty <= reorder;
                    return (
                        <span className={isLow ? 'low-stock-warning' : ''}>
                            {qty.toFixed(2)} <small className="text-muted">({itemCount})</small>
                            {isLow && <i className="fa fa-exclamation-triangle ml-2"></i>}
                        </span>
                    );
                }
            },
            {
                dataField: 'reorder_point',
                text: 'Reorder Point',
                formatter: (cell) => cell ? parseFloat(cell).toFixed(0) : '-'
            },
            {
                dataField: 'actions',
                text: '',
                formatter: (cell, row) => (
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => { e.stopPropagation(); this.handleOpenEditProduct(row); }}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                ),
                headerStyle: { width: '60px' }
            }
        ];

        const expandRow = {
            renderer: (row) => {
                const rowId = row.product_id || row.id;
                const items = productItems[rowId] || [];

                if (items.length === 0) {
                    return (
                        <div className="inventory-items-details">
                            <p className="text-muted text-center">No items found for this product</p>
                        </div>
                    );
                }

                return (
                    <div className="inventory-items-details">
                        <div className="items-header">
                            <strong>Inventory Items ({items.length})</strong>
                        </div>
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>Barcode</th>
                                    <th>Amount Remaining</th>
                                    <th>Date Received</th>
                                    <th>Usage</th>
                                    <th style={{ width: '60px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => {
                                    const usageCount = parseInt(item.usage_count) || 0;
                                    return (
                                        <tr key={index}>
                                            <td><code>{item.barcode}</code></td>
                                            <td>{parseFloat(item.quantity_remaining || 0).toFixed(2)}</td>
                                            <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                                            <td>
                                                {usageCount === 0 ? (
                                                    <span className="text-muted">No usage</span>
                                                ) : (
                                                    <a
                                                        href="#/"
                                                        className="usage-link"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            this.handleOpenItemUsageModal(item, row);
                                                        }}
                                                    >
                                                        {usageCount} {usageCount === 1 ? 'job' : 'jobs'}
                                                    </a>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => this.handleOpenAdjustModal(item, rowId)}
                                                    title="Adjust quantity"
                                                >
                                                    <i className="fa fa-pencil"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            },
            expanded: expandedRows,
            onExpand: (row, isExpand) => {
                const rowId = row.product_id || row.id;
                if (isExpand) {
                    this.handleToggleExpand(rowId);
                } else {
                    this.setState({
                        expandedRows: expandedRows.filter(id => id !== rowId)
                    });
                }
            },
            showExpandColumn: false,
            expandByColumnOnly: true
        };

        const rowClasses = (row) => {
            const qty = parseFloat(row.quantity_on_hand || row.total_quantity || row.quantity_in_stock || row.stock_quantity || row.quantity) || 0;
            const reorder = parseFloat(row.reorder_point) || 0;
            if (reorder > 0 && qty <= reorder) {
                return 'low-stock-row';
            }
            return '';
        };

        return (
            <React.Fragment>
                <div className={`inventory-container ${isDesktop ? 'desktop' : 'mobile'}`}>
                    {this.renderOverviewCards()}
                    {this.renderFiltersSection()}
                    {this.renderActionButtons()}

                    <div className="table-container">
                        <BootstrapTable
                            keyField='id'
                            data={filteredProducts}
                            columns={columns}
                            expandRow={expandRow}
                            rowClasses={rowClasses}
                            noDataIndication="No products found"
                        />
                    </div>
                </div>

                {this.renderProductModal()}
                {this.renderAdjustModal()}
                {this.renderUsageModal()}

                <style>{`
                    .inventory-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: 24px;
                        background: #f8fafc;
                        min-height: 100vh;
                    }

                    .inventory-container.mobile {
                        padding: 16px;
                    }

                    .inventory-header {
                        margin-bottom: 24px;
                    }

                    .inventory-header h4 {
                        color: #374151;
                        margin: 0;
                    }

                    .error-message {
                        background: white;
                        border: 1px solid #fecaca;
                        border-radius: 8px;
                        padding: 24px;
                        text-align: center;
                        color: #dc2626;
                    }

                    .error-message button {
                        background: #dc2626;
                        color: white;
                        border: none;
                        padding: 8px 16px;
                        border-radius: 4px;
                        cursor: pointer;
                        margin-top: 12px;
                    }

                    .overview-cards {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 16px;
                        margin-bottom: 24px;
                    }

                    .mobile .overview-cards {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 12px;
                    }

                    .overview-card {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 20px;
                        text-align: center;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }

                    .overview-card.warning .number {
                        color: #f59e0b;
                    }

                    .overview-card .number {
                        font-size: 24px;
                        font-weight: 700;
                        color: #374151;
                        margin-bottom: 4px;
                    }

                    .overview-card .label {
                        font-size: 12px;
                        color: #6b7280;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .filters-section {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 20px;
                        margin-bottom: 16px;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                        display: flex;
                        gap: 24px;
                        flex-wrap: wrap;
                        align-items: flex-end;
                    }

                    .search-controls {
                        flex: 1;
                        min-width: 250px;
                    }

                    .search-group label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        display: block;
                        margin-bottom: 4px;
                    }

                    .search-input-group {
                        position: relative;
                        display: flex;
                        align-items: center;
                    }

                    .search-input {
                        width: 100%;
                        padding: 8px 12px;
                        border: 1px solid #d1d5db;
                        border-radius: 6px;
                        font-size: 14px;
                    }

                    .search-input:focus {
                        outline: none;
                        border-color: #3FB2BE;
                        box-shadow: 0 0 0 2px rgba(63, 178, 190, 0.1);
                    }

                    .clear-search-btn {
                        position: absolute;
                        right: 8px;
                        background: none;
                        border: none;
                        font-size: 18px;
                        color: #6b7280;
                        cursor: pointer;
                    }

                    .filter-controls {
                        display: flex;
                        gap: 16px;
                    }

                    .filter-group label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        display: block;
                        margin-bottom: 4px;
                    }

                    .filter-group select {
                        min-width: 180px;
                    }

                    .action-buttons {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 16px;
                        margin-bottom: 24px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        flex-wrap: wrap;
                    }

                    .action-buttons .label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                    }

                    .action-btn {
                        background: white;
                        border: 1px solid #e5e7eb;
                        color: #6b7280;
                        padding: 8px 16px;
                        border-radius: 6px;
                        font-size: 13px;
                        font-weight: 500;
                        cursor: pointer;
                        text-decoration: none;
                        display: inline-flex;
                        align-items: center;
                        transition: all 0.2s ease;
                    }

                    .action-btn:hover {
                        border-color: #d1d5db;
                        background: #fafafa;
                        color: #374151;
                        text-decoration: none;
                    }

                    .action-btn.primary-btn {
                        background: #3FB2BE;
                        color: white;
                        border-color: #3FB2BE;
                    }

                    .action-btn.primary-btn:hover {
                        background: #359ca6;
                        border-color: #359ca6;
                        color: white;
                    }

                    .table-container {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }

                    .table-container .react-bootstrap-table table th {
                        background: #f8fafc;
                        border-bottom: 2px solid #e5e7eb;
                        border-top: none;
                        padding: 12px 16px;
                        font-weight: 600;
                        color: #374151;
                        font-size: 13px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .table-container .react-bootstrap-table table td {
                        padding: 12px 16px;
                        border-bottom: 1px solid #f3f4f6;
                        border-top: none;
                        vertical-align: middle;
                    }

                    .table-container .react-bootstrap-table table tbody tr:hover {
                        background-color: #f9fafb;
                    }

                    .low-stock-row {
                        background-color: #fef3c7 !important;
                    }

                    .low-stock-warning {
                        color: #d97706;
                        font-weight: 600;
                    }

                    .expand-toggle {
                        cursor: pointer;
                        color: #6b7280;
                        font-size: 12px;
                        padding: 4px;
                    }

                    .expand-toggle:hover {
                        color: #374151;
                    }

                    .inventory-items-details {
                        background: #f8fafc;
                        padding: 16px 20px;
                        border-left: 4px solid #3FB2BE;
                    }

                    .items-header {
                        margin-bottom: 12px;
                        color: #374151;
                        font-size: 14px;
                    }

                    .items-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 13px;
                        background: white;
                        border-radius: 6px;
                        overflow: hidden;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    }

                    .items-table th {
                        background: #f1f5f9;
                        padding: 10px 12px;
                        text-align: left;
                        font-weight: 600;
                        color: #475569;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 1px solid #e2e8f0;
                    }

                    .items-table td {
                        padding: 10px 12px;
                        border-bottom: 1px solid #f1f5f9;
                        color: #374151;
                    }

                    .items-table tr:last-child td {
                        border-bottom: none;
                    }

                    .status-badge {
                        display: inline-block;
                        padding: 3px 8px;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: 500;
                    }

                    .status-active {
                        background: #dcfce7;
                        color: #166534;
                    }

                    .status-depleted {
                        background: #fecaca;
                        color: #991b1b;
                    }

                    /* Usage link styles */
                    .usage-link {
                        color: #3FB2BE;
                        text-decoration: none;
                        font-weight: 500;
                    }

                    .usage-link:hover {
                        color: #359ca6;
                        text-decoration: underline;
                    }

                    /* Usage modal styles */
                    .usage-product-info {
                        padding: 12px 16px;
                        background: #f8fafc;
                        border-radius: 6px;
                        border-left: 4px solid #3FB2BE;
                    }

                    .usage-table-container {
                        max-height: 400px;
                        overflow-y: auto;
                    }

                    .usage-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 13px;
                    }

                    .usage-table th {
                        background: #f1f5f9;
                        padding: 10px 12px;
                        text-align: left;
                        font-weight: 600;
                        color: #475569;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        position: sticky;
                        top: 0;
                    }

                    .usage-table td {
                        padding: 10px 12px;
                        border-bottom: 1px solid #f1f5f9;
                        color: #374151;
                    }

                    .usage-table tr:hover {
                        background-color: #f9fafb;
                    }

                    .usage-table a {
                        color: #3FB2BE;
                        text-decoration: none;
                        font-weight: 500;
                    }

                    .usage-table a:hover {
                        text-decoration: underline;
                    }

                    @media (max-width: 768px) {
                        .filters-section {
                            flex-direction: column;
                            gap: 16px;
                        }

                        .filter-controls {
                            width: 100%;
                        }

                        .filter-group {
                            flex: 1;
                        }

                        .filter-group select {
                            width: 100%;
                            min-width: auto;
                        }

                        .action-buttons {
                            flex-direction: column;
                            align-items: stretch;
                        }

                        .action-btn {
                            justify-content: center;
                        }
                    }
                `}</style>
            </React.Fragment>
        );
    }
}

export default InventoryDashboard;
